import { Box, Chip, Typography } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import moment from "jalali-moment";
import { useMemo } from "react";
import renderCellExpand from "src/GridComponents/GridExpand";
import { ListOfStatusGlobal } from "src/interface/enum/StatusGlobal";
import { BootstrapTooltip } from "src/utilities";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import PersonIcon from "@mui/icons-material/Person";
import { IAutoCompleteLabelValue } from "src/interface/enum/GeneralType";

interface ColumnsProps {
  handleEditStatusClick: (row: any) => () => void;
  handleEditUserClick?: (userId: string) => void;
  getCustomers: IAutoCompleteLabelValue[];
  getEditors: IAutoCompleteLabelValue[];
}

export const useColumns = ({
  handleEditStatusClick,
  handleEditUserClick,
  getCustomers,
  getEditors,
}: ColumnsProps): GridColDef[] => {
  return useMemo(
    () => [
      {
        field: "actions",
        type: "actions",
        headerAlign: "center",
        headerName: "عملیات",
        align: "center",
        sortable: false,
        disableColumnMenu: true,
        cellClassName: "actions",
        minWidth: 150,
        getActions: (params) => [
          <BootstrapTooltip title="تغییر وضعیت">
            <GridActionsCellItem
              icon={<SyncProblemIcon color="warning" />}
              label="تغییر وضعیت"
              className="textPrimary"
              onClick={handleEditStatusClick(params.row)}
              color="inherit"
            />
          </BootstrapTooltip>,
          <BootstrapTooltip title="ویرایش کاربر">
            <GridActionsCellItem
              icon={<PersonIcon color="info" />}
              label="ویرایش کاربر"
              color="inherit"
              onClick={() => {
                if (handleEditUserClick && params.row.customer?.id) {
                  handleEditUserClick(params.row.customer.id);
                }
              }}
            />
          </BootstrapTooltip>,
        ],
      },
      {
        field: "desc",
        headerName: "توضیحات",
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
        minWidth: 200,
        valueGetter: (value: any, row: any) => {
          // ترکیب توضیحات اصلی و توضیحات وضعیت برای جستجو؛ فقط وقتی هر دو مقدار دارند
          const mainDesc = value || "";
          const statusDesc = row.statusDesc || "";
          if (mainDesc && statusDesc) return `${mainDesc} ${statusDesc}`.trim();
          return "";
        },
        // renderCell: (params: GridRenderCellParams<any, string>) => {
        //   return (
        //     <Box>
        //       <Typography variant="body2" style={{ whiteSpace: "pre-line" }}>
        //         {params.value}
        //       </Typography>
        //       {params.row.statusDesc && (
        //         <Typography
        //           variant="caption"
        //           color="text.secondary"
        //           style={{
        //             whiteSpace: "pre-line",
        //             display: "block",
        //             marginTop: "4px",
        //           }}
        //         >
        //           <strong>وضعیت:</strong> {params.row.statusDesc}
        //         </Typography>
        //       )}
        //     </Box>
        //   );
        // },
      },
      {
        field: "priority",
        headerName: "اولویت",
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
      },
      {
        field: "titleTask",
        headerName: "عنوان",
        align: "left",
        headerAlign: "left",
        editable: false,
        sortable: true,
        minWidth: 180,
      },
      {
        field: "customerRef",
        headerName: "مشتری",
        align: "left",
        headerAlign: "left",
        editable: false,
        sortable: true,
        type: "singleSelect",
        valueOptions: getCustomers,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<any, string>) => {
          return (
            <Typography style={{ whiteSpace: "pre-line" }}>
              {params.row.customer?.fName + " " + params.row.customer?.lName}
              {"\n"}
              {params.row.customer?.coName}
            </Typography>
          );
        },
      },
      {
        field: "mobile",
        headerName: "موبایل",
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
        minWidth: 180,
        renderCell: (params: GridRenderCellParams<any, string>) =>
          params.row.customer?.phoneNumber,
      },
      {
        field: "statusTask",
        headerName: "وضعیت",
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
        type: "singleSelect",
        valueOptions: ListOfStatusGlobal,
        minWidth: 150,
        renderCell: (params) => {
          const status = ListOfStatusGlobal.find(
            (g) => g.value === params.value
          );
          return (
            <Chip
              label={status?.label}
              //@ts-ignore
              color={status?.color}
              variant="filled"
            />
          );
        },
      },
      {
        field: "dueDate",
        headerName: "تاریخ سر رسید",
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
        type: "date",
        minWidth: 150,
        valueGetter: (params) => (params ? new Date(params) : null),
        renderCell: (params: GridRenderCellParams<any, string>) => {
          if (!params.value) return "-";

          const dueDate = moment(params.value, "YYYY-MM-DD");
          const today = moment().startOf("day");
          const diffDays = dueDate.diff(today, "days");

          return (
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2">
                {moment
                  .from(params.value, "en", "YYYY-MM-DD")
                  .format("jYYYY/jMM/jDD")}
              </Typography>
              {diffDays > 0 ? (
                <Chip
                  label={`${diffDays} روز باقی‌مانده`}
                  color="success"
                  variant="filled"
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              ) : diffDays < 0 ? (
                <Chip
                  label={`${Math.abs(diffDays)} روز گذشته`}
                  color="error"
                  variant="filled"
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              ) : (
                <Chip
                  label="امروز سررسید"
                  color="warning"
                  variant="filled"
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
          );
        },
      },

      {
        field: "userRef",
        headerName: "کارمند مرتبط",
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
        type: "singleSelect",
        valueOptions: getEditors,
        minWidth: 180,
        renderCell: (params) => {
          // مطمئن می‌شیم که params.value یه رشته باشه و اگه نبود، خالی برگردونیم
          const value = params.value?.toString() || "";
          const editor =
            getEditors?.find((e) => e.value === value)?.label || "";

          // حروف اول رو جدا می‌کنیم و با فاصله به هم وصل می‌کنیم
          const initials = editor
            .split(" ") // جدا کردن کلمات با فاصله
            .map((word) => word.charAt(0).toUpperCase()) // گرفتن حرف اول و بزرگ کردنش
            .join(" "); // وصل کردن با فاصله

          return (
            <BootstrapTooltip title={editor}>
              <p>{initials}</p>
            </BootstrapTooltip>
          );
        },
      },
    ],
    [getCustomers, getEditors, handleEditStatusClick, handleEditUserClick]
  );
};
